import { useState, useEffect } from "react";
import { MapPin, Check, Map, List } from "lucide-react";
import type { SetupStepProps } from "./types";
import { calculateDistance, isZipCode } from "./utils";
import { geocodeZipCode } from "../../utils/geocoding";
import { formatHomeDepotStoreId } from "../../utils/storeUtils";
import { useStores } from "../../hooks/useStores";
import type { WalmartStore, HomeDepotStore } from "../../utils/storeUtils";
import MapStoreSelector from "./MapStoreSelector";

interface StoreWithDistance {
  storeId: string;
  address: string;
  city: string;
  state: string;
  postal_code: string;
  distance: number;
  geo?: {
    lat: number;
    long: number;
  };
}

interface HomeDepotStoreWithDistance extends HomeDepotStore {
  distance: number;
  storeId: string;
}

interface StoresSelectorProps extends SetupStepProps {
  selectedStoreIds: string[];
  onStoreSelect: (storeId: string) => void;
  maxStores: number;
  retailer: "walmart" | "home_depot";
  zipCode: string;
}

export default function StoresSelector({
  selectedStoreIds,
  onStoreSelect,
  maxStores,
  retailer,
  setError,
  zipCode,
}: StoresSelectorProps) {
  const [isGeocodingLoading, setIsGeocodingLoading] = useState(false);
  const [nearbyStores, setNearbyStores] = useState<StoreWithDistance[]>([]);
  const [nearbyHomeDepotStores, setNearbyHomeDepotStores] = useState<
    HomeDepotStoreWithDistance[]
  >([]);
  const [viewMode, setViewMode] = useState<"list" | "map">("map");
  const [coordinates, setCoordinates] = useState<
    { lat: number; long: number } | undefined
  >(undefined);

  // Load stores from S3
  const { stores: walmartStores } = useStores<WalmartStore[]>("walmart");
  const { stores: homeDepotStores } = useStores<HomeDepotStore[]>("home_depot");
  // const [searchTerm, setSearchTerm] = useState("");

  // Handle async zip code search for both retailers
  useEffect(() => {
    // Debounce the search to avoid running on every keystroke
    const timeoutId = setTimeout(() => {
      const searchZipCode = async () => {
        if (!isZipCode(zipCode)) {
          setNearbyStores([]);
          setNearbyHomeDepotStores([]);
          return;
        }

        // Only search if we have a complete 5-digit zip code
        if (zipCode.length !== 5) {
          return;
        }

        setIsGeocodingLoading(true);
        try {
          const coords = await geocodeZipCode(zipCode);
          setCoordinates(coords || undefined);

          if (coords) {
            if (retailer === "walmart" && walmartStores) {
              const allStores = walmartStores;

              // Calculate distances to all stores and filter within 50 miles
              const storesWithDistance: StoreWithDistance[] = allStores
                .filter(
                  (
                    store
                  ): store is WalmartStore & {
                    geo: { lat: number; long: number };
                  } => !!(store.geo && store.geo.lat && store.geo.long)
                ) // Only stores with valid geo data
                .map((store) => {
                  const distance = calculateDistance(
                    coords.lat,
                    coords.long,
                    store.geo.lat,
                    store.geo.long
                  );
                  return {
                    storeId: store.store_id,
                    address: store.address,
                    city: store.city,
                    state: store.state,
                    postal_code: store.postal_code,
                    distance,
                    geo: store.geo,
                  };
                })
                .filter((store) => store.distance <= 50) // Within 50 miles
                .sort((a, b) => a.distance - b.distance); // Sort by distance

              setNearbyStores(storesWithDistance);
            } else if (retailer === "home_depot" && homeDepotStores) {
              const allStores = homeDepotStores;

              // Calculate distances to all stores and filter within 50 miles
              const storesWithDistance: HomeDepotStoreWithDistance[] = allStores
                .filter((store) => store.latitude && store.longitude) // Only stores with valid coordinates
                .map((store) => {
                  const distance = calculateDistance(
                    coords.lat,
                    coords.long,
                    parseFloat(store.latitude),
                    parseFloat(store.longitude)
                  );
                  return {
                    ...store,
                    distance,
                    storeId: formatHomeDepotStoreId(store.store_number),
                    id: formatHomeDepotStoreId(store.store_number),
                  };
                })
                .filter((store) => store.distance <= 50) // Within 50 miles
                .sort((a, b) => a.distance - b.distance); // Sort by distance

              setNearbyHomeDepotStores(storesWithDistance);
            }
          }
        } catch (error) {
          console.error("Error geocoding zip code:", error);
          setNearbyStores([]);
          setNearbyHomeDepotStores([]);
        } finally {
          setIsGeocodingLoading(false);
        }
      };

      searchZipCode();
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [zipCode, retailer, walmartStores, homeDepotStores]);

  const handleStoreSelect = (storeId: string) => {
    const isSelected = selectedStoreIds.includes(storeId);

    if (isSelected) {
      // Deselect the store
      onStoreSelect(storeId);
    } else {
      // Select the store (if under limit)
      if (selectedStoreIds.length >= maxStores) {
        setError(
          `You can only select up to ${maxStores} ${
            retailer === "walmart" ? "Walmart" : "HD"
          } stores.`
        );
        return;
      }
      onStoreSelect(storeId);
    }
    setError("");
  };

  const retailerName = retailer === "walmart" ? "Walmart" : "HD";

  return (
    <div className="max-w-4xl mx-auto h-full flex flex-col space-y-4">
      <div className="text-center space-y-3">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500/10 to-purple-600/10 backdrop-blur-sm border border-purple-200/40 shadow-sm">
            <MapPin className="w-4.5 h-4.5 text-purple-600" strokeWidth={2.5} />
          </div>
          <div className="text-left flex-1">
            <h1 className="text-base font-bold text-gray-900 leading-tight">
              Choose {retailerName} Stores
            </h1>
            <p className="text-xs text-gray-600 mt-0.5">
              Showing stores around {zipCode}
              {isGeocodingLoading && (
                <span className="inline-flex items-center ml-1.5">
                  <div className="w-2.5 h-2.5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                </span>
              )}
            </p>
          </div>

          {/* View Toggle */}
          {(nearbyStores.length > 0 || nearbyHomeDepotStores.length > 0) && (
            <div className="flex bg-gray-100 p-0.5 rounded-lg border border-gray-200">
              <button
                onClick={() => setViewMode("map")}
                className={`p-1.5 rounded-md transition-all ${
                  viewMode === "map"
                    ? "bg-white text-purple-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                title="Map View"
              >
                <Map className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-1.5 rounded-md transition-all ${
                  viewMode === "list"
                    ? "bg-white text-purple-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                title="List View"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0">
        {/* Map View */}
        {viewMode === "map" &&
          ((retailer === "walmart" && nearbyStores.length > 0) ||
            (retailer === "home_depot" &&
              nearbyHomeDepotStores.length > 0)) && (
            <div
              className="flex-1 flex flex-col"
              style={{ minHeight: "350px" }}
            >
              {/* Store Selection Counter */}
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold text-gray-900">
                    {selectedStoreIds.length}/{maxStores} selected
                  </span>
                  {selectedStoreIds.length >= maxStores && (
                    <span className="inline-flex items-center px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                      Limit reached
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-500">
                  {retailer === "walmart"
                    ? `${nearbyStores.length} nearby`
                    : `${nearbyHomeDepotStores.length} nearby`}
                </span>
              </div>
              <MapStoreSelector
                stores={
                  retailer === "walmart" ? nearbyStores : nearbyHomeDepotStores
                }
                selectedStoreIds={selectedStoreIds}
                onStoreSelect={handleStoreSelect}
                retailer={retailer}
                center={coordinates}
                height="350px"
                maxStores={maxStores}
              />
            </div>
          )}

        {/* List View */}
        {viewMode === "list" &&
          ((retailer === "walmart" &&
            nearbyStores.length > 0 &&
            zipCode &&
            isZipCode(zipCode) &&
            !isGeocodingLoading) ||
            (retailer === "home_depot" &&
              nearbyHomeDepotStores.length > 0 &&
              zipCode &&
              isZipCode(zipCode) &&
              !isGeocodingLoading)) && (
            <div className="flex-1 flex flex-col min-h-0">
              <div className="bg-gradient-to-br from-gray-50/80 to-gray-100/80 backdrop-blur-sm border border-gray-200/60 rounded-2xl overflow-hidden shadow-sm flex flex-col max-h-[400px]">
                {/* Table Header */}
                <div className="bg-gradient-to-br from-white/60 to-gray-50/60 backdrop-blur-sm border-b border-gray-200/60 px-3 py-2.5 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-semibold text-gray-900">
                      {retailer === "walmart"
                        ? `Nearby Stores (${nearbyStores.length})`
                        : `Nearby Stores (${nearbyHomeDepotStores.length})`}
                    </h3>
                    {selectedStoreIds.length > 0 && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-gradient-to-br from-purple-50/80 to-purple-100/80 backdrop-blur-sm border border-purple-200/40 rounded-lg text-xs text-purple-900 font-semibold">
                        <Check className="w-3 h-3" />
                        {selectedStoreIds.length}/{maxStores}
                      </span>
                    )}
                  </div>
                </div>

                {/* Stores List */}
                <div className="flex-1 overflow-y-auto min-h-0">
                  {retailer === "walmart"
                    ? nearbyStores.map((store) => {
                        const isSelected = selectedStoreIds.includes(
                          store.storeId
                        );
                        const isDisabled =
                          !isSelected && selectedStoreIds.length >= maxStores;

                        return (
                          <div
                            key={store.storeId}
                            className={`flex items-center gap-3 px-3 py-2.5 border-b border-gray-100/60 last:border-b-0 transition-all duration-200 ${
                              isDisabled
                                ? "opacity-50 cursor-not-allowed"
                                : "cursor-pointer"
                            } ${
                              isSelected
                                ? "bg-gradient-to-br from-purple-50/80 to-purple-100/80 backdrop-blur-sm hover:from-purple-100/80 hover:to-purple-200/80"
                                : isDisabled
                                ? ""
                                : "hover:bg-white/60 backdrop-blur-sm"
                            }`}
                            onClick={() => {
                              if (!isDisabled) {
                                handleStoreSelect(store.storeId);
                              }
                            }}
                          >
                            <div className="flex-shrink-0 pt-0.5">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={isDisabled}
                                onChange={() => {}} // Controlled by parent div click
                                className={`w-4 h-4 accent-purple-600 bg-white border-gray-300 rounded focus:ring-purple-500 focus:ring-2 transition-colors touch-manipulation pointer-events-none ${
                                  isDisabled
                                    ? "opacity-50"
                                    : "hover:border-purple-400"
                                }`}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-start gap-2">
                                <div className="min-w-0">
                                  <p
                                    className={`text-sm font-medium truncate transition-colors ${
                                      isSelected
                                        ? "text-purple-900"
                                        : isDisabled
                                        ? "text-gray-400"
                                        : "text-gray-900"
                                    }`}
                                  >
                                    {store.address}
                                  </p>
                                  <p
                                    className={`text-xs truncate mt-0.5 ${
                                      isDisabled
                                        ? "text-gray-400"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    {store.city} {store.state}{" "}
                                    {store.postal_code}
                                  </p>
                                </div>
                                <span
                                  className={`text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                                    isSelected
                                      ? "text-purple-700"
                                      : isDisabled
                                      ? "text-gray-400"
                                      : "text-gray-400"
                                  }`}
                                >
                                  {store.distance.toFixed(1)} mi
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    : nearbyHomeDepotStores.map((store) => {
                        const isSelected = selectedStoreIds.includes(
                          store.storeId
                        );
                        const isDisabled =
                          !isSelected && selectedStoreIds.length >= maxStores;

                        const addressParts = [
                          store.address_1,
                          store.city,
                          store.state,
                        ].filter(Boolean);
                        const addressText =
                          addressParts.join(", ") +
                          (store.zip ? ` ${store.zip}` : "");

                        return (
                          <div
                            key={store.store_number}
                            className={`flex items-center gap-3 px-3 py-2.5 border-b border-gray-100/60 last:border-b-0 transition-all duration-200 ${
                              isDisabled
                                ? "opacity-50 cursor-not-allowed"
                                : "cursor-pointer"
                            } ${
                              isSelected
                                ? "bg-gradient-to-br from-purple-50/80 to-purple-100/80 backdrop-blur-sm hover:from-purple-100/80 hover:to-purple-200/80"
                                : isDisabled
                                ? ""
                                : "hover:bg-white/60 backdrop-blur-sm"
                            }`}
                            onClick={() => {
                              if (!isDisabled) {
                                handleStoreSelect(store.storeId);
                              }
                            }}
                          >
                            <div className="flex-shrink-0 pt-0.5">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={isDisabled}
                                onChange={() => {}} // Controlled by parent div click
                                className={`w-4 h-4 accent-purple-600 bg-white border-gray-300 rounded focus:ring-purple-500 focus:ring-2 transition-colors touch-manipulation pointer-events-none ${
                                  isDisabled
                                    ? "opacity-50"
                                    : "hover:border-purple-400"
                                }`}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-start gap-2">
                                <div className="min-w-0">
                                  <p
                                    className={`text-sm font-medium truncate transition-colors ${
                                      isSelected
                                        ? "text-purple-900"
                                        : isDisabled
                                        ? "text-gray-400"
                                        : "text-gray-900"
                                    }`}
                                  >
                                    {store.store_title}
                                  </p>
                                  <p
                                    className={`text-xs truncate mt-0.5 ${
                                      isDisabled
                                        ? "text-gray-400"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    {addressText}
                                  </p>
                                </div>
                                <span
                                  className={`text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                                    isSelected
                                      ? "text-purple-700"
                                      : isDisabled
                                      ? "text-gray-400"
                                      : "text-gray-400"
                                  }`}
                                >
                                  {store.distance.toFixed(1)} mi
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                </div>
              </div>
            </div>
          )}

        {/* No Stores Message */}
        {((retailer === "walmart" &&
          nearbyStores.length === 0 &&
          zipCode &&
          isZipCode(zipCode) &&
          !isGeocodingLoading) ||
          (retailer === "home_depot" &&
            nearbyHomeDepotStores.length === 0 &&
            zipCode &&
            isZipCode(zipCode) &&
            !isGeocodingLoading)) && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center px-6 py-8 bg-gradient-to-br from-gray-50/80 to-gray-100/80 backdrop-blur-sm border border-gray-200/60 rounded-2xl shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-gray-100/60 to-gray-200/60 backdrop-blur-sm border border-gray-300/40 mx-auto mb-3">
                <MapPin className="w-6 h-6 text-gray-400" strokeWidth={2} />
              </div>
              <p className="text-gray-600 text-xs font-medium">
                No stores found within 50 miles
              </p>
              <p className="text-gray-500 text-xs mt-1">
                Try a different zip code
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
      </div>
    </div>
  );
}
